/**
 * Buckets und Zeitachse (HM-099).
 *
 * **Ein Balken ist ein Zeitabschnitt, kein Zeitpunkt.** Ein Punkt der Reihe
 * trägt den *Beginn* seines Buckets (`ChartPoint.at`); der Balken für die
 * Stunde 14–15 Uhr reicht von 14:00 bis 15:00 und steht nicht mittig über
 * 14:00. Genau das tat er bis HM-099: Die Zeichenbibliothek setzte jeden
 * Balken zentriert auf seinen Zeitpunkt — der erste Balken eines Tages war
 * halb abgeschnitten, hinter dem letzten blieb eine halbe Balkenbreite leer,
 * und wer die Achse las, ordnete jeden Balken der falschen Stunde zu.
 *
 * **Und ein Tag ist kein Vielfaches von Millisekunden.** Ein lokaler Tag hat
 * 23, 24 oder 25 Stunden, ein Monat 28 bis 31 Tage. Wo ein Bucket endet, sagen
 * deshalb die Helfer aus `@hausmeter/timeseries` (Regel 3) und keine Zahl;
 * die Zahlen in `BUCKET_MS` sind Nennweiten für die Lückensuche und für die
 * Minutenraster, in denen es keine Umstellung gibt.
 *
 * **Die Beschriftung folgt der Spanne, nicht der Auflösung.** Über einen Tag
 * will man die Uhrzeit lesen, über eine Woche den Wochentag, über einen Monat
 * den Tag, über ein Jahr den Monat. Stunden stehen an den **Kanten** — die Zeit
 * läuft durch, und „14:00" links vom Balken heißt „ab 14 Uhr". Tage und Monate
 * sind benannte Einheiten und stehen **mittig** unter ihrem Balken: „Mo" gehört
 * zum Balken, nicht zu seiner linken Kante. Und was nicht nebeneinander passt,
 * wird ausgedünnt — jeder zweite Tag, jeder dritte Monat — statt übereinander
 * geschrieben.
 *
 * Reine Rechnung, ohne uPlot und ohne React.
 */
import type { Locale } from "@hausmeter/i18n";
import {
  isoWeekOf,
  localDaysBetween,
  localMonthsBetween,
  partsInZone,
  startOfNextLocalDay,
  startOfNextLocalHourBlock,
  startOfNextLocalMonth,
  startOfNextLocalWeek,
  zonedPartsToUtc,
  type LocalHourBlock,
} from "@hausmeter/timeseries";

/**
 * Die Rasterweiten des Vertrags (`Resolution`) — Minuten, ein lokaler Tag, ein
 * lokaler Monat — und dazu die **zusammengefassten Stunden** (`merge.ts`):
 * lokale Blöcke aus 2, 3, 4 oder 6 Stunden, die nur das Diagramm bildet, wenn
 * 24 Stundenbalken nicht in seine Breite passen. Der Server kennt sie nicht.
 */
export type ChartBucket = "5m" | "15m" | "1h" | MergedHours | "1d" | "1w" | "1M";
export type MergedHours = "2h" | "3h" | "4h" | "6h";

/** Die Stunden eines zusammengefassten Rasters — oder `null` für jedes andere. */
export function mergedHoursOf(bucket: ChartBucket | null | undefined): Exclude<LocalHourBlock, 1 | 12> | null {
  switch (bucket) {
    case "2h":
      return 2;
    case "3h":
      return 3;
    case "4h":
      return 4;
    case "6h":
      return 6;
    default:
      return null;
  }
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Die Nennweite je Raster in Millisekunden.
 *
 * Für Tage und Monate ein **Näherungswert**: Er dient der Lückensuche
 * (`align.ts`, mit Spielraum) und dem Rückfall, wo kein Fenster bekannt ist —
 * nie dem Zeichnen eines Balkens. Der endet an der nächsten lokalen Tages-
 * oder Monatsgrenze.
 */
export const BUCKET_MS: Record<ChartBucket, number> = {
  "5m": 5 * MINUTE,
  "15m": 15 * MINUTE,
  "1h": HOUR,
  "2h": 2 * HOUR,
  "3h": 3 * HOUR,
  "4h": 4 * HOUR,
  "6h": 6 * HOUR,
  "1d": DAY,
  "1w": 7 * DAY,
  "1M": 30 * DAY,
};

/** Wo der Bucket endet, der bei `at` beginnt — in der Zeitzone der Anlage. */
export function bucketEnd(at: number, bucket: ChartBucket, timeZone: string): number {
  const merged = mergedHoursOf(bucket);
  /* Ein Stundenblock folgt der Uhr der Anlage und ist am Umstellungstag kürzer oder länger. */
  if (merged !== null) return startOfNextLocalHourBlock(new Date(at), merged, timeZone).getTime();
  switch (bucket) {
    case "1d":
      return startOfNextLocalDay(new Date(at), timeZone).getTime();
    /* Eine Woche beginnt montags; kein Zeitraum außer der Nutzungsstatistik rechnet in Wochen (HM-174). */
    case "1w":
      return startOfNextLocalWeek(new Date(at), timeZone).getTime();
    case "1M":
      return startOfNextLocalMonth(new Date(at), timeZone).getTime();
    default:
      return at + BUCKET_MS[bucket];
  }
}

/**
 * Das Ende des Abschnitts an Stelle `index` einer Zeitachse.
 *
 * Mit bekanntem Raster ist es die Bucket-Grenze. Ohne Raster gilt der nächste
 * Zeitpunkt der Achse, solange er nicht weiter als anderthalb Rasterschritte
 * entfernt liegt — dahinter beginnt eine Lücke (`align.ts`), und der Balken
 * endet nach einem Schritt.
 */
export function spanEnd(
  times: readonly number[],
  index: number,
  options: {
    readonly bucket: ChartBucket | null;
    readonly step: number;
    readonly timeZone: string;
  },
): number {
  const at = times[index]!;
  if (options.bucket !== null) return bucketEnd(at, options.bucket, options.timeZone);
  const next = times[index + 1];
  if (next !== undefined && next - at <= options.step * 1.5) return next;
  return at + options.step;
}

/**
 * Der Abschnitt, in dem ein Zeitpunkt liegt — oder `null`, wenn er in keinem
 * liegt (hinter dem letzten Bucket eines angebrochenen Tages, vor dem ersten).
 *
 * Das ist die Regel für das Ablesefenster über Balken: Gefragt wird nicht nach
 * dem *nächsten* Zeitpunkt, sondern nach dem Balken, über dem der Zeiger steht.
 * Mit „nächster Zeitpunkt" gehörte die rechte Hälfte jedes Balkens schon zum
 * folgenden — und der abgelesene Wert war der des Nachbarn.
 */
export function spanAt(
  times: readonly number[],
  at: number,
  options: {
    readonly bucket: ChartBucket | null;
    readonly step: number;
    readonly timeZone: string;
  },
): number | null {
  let low = 0;
  let high = times.length - 1;
  if (high < 0 || at < times[0]!) return null;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (times[mid]! <= at) low = mid;
    else high = mid - 1;
  }
  return at < spanEnd(times, low, options) ? low : null;
}

/* ---- Beschriftung ------------------------------------------------------- */

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function formatter(
  locale: Locale,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone}|${JSON.stringify(options)}`;
  let cached = FORMATTERS.get(key);
  if (cached === undefined) {
    cached = new Intl.DateTimeFormat(locale, { timeZone, ...options });
    FORMATTERS.set(key, cached);
  }
  return cached;
}

/** „Mo." wird zu „Mo" — der Punkt trägt in einer Achsenbeschriftung nichts. */
function trimDot(text: string): string {
  return text.replace(/\.$/, "");
}

/**
 * Die Überschrift des Ablesefensters für einen Bucket.
 *
 * Ein Tagesbalken heißt „Mittwoch, 2. September 2026" und nicht
 * „02.09.2026, 00:00": Die Mitternacht ist sein Beginn, nicht sein Inhalt. Ein
 * Stundenbalken nennt beide Kanten, denn „14:00" allein liest sich als
 * Zeitpunkt.
 */
export function bucketLabel(
  at: number,
  bucket: ChartBucket,
  timeZone: string,
  locale: Locale,
): string {
  const start = new Date(at);
  switch (bucket) {
    case "1M":
      return formatter(locale, timeZone, { month: "long", year: "numeric" }).format(start);
    case "1w": {
      /* „KW 41 · 5.10. – 11.10.2026": der Sonntag, nicht der nächste Montag, ist der letzte Tag. */
      const last = new Date(bucketEnd(at, bucket, timeZone) - HOUR);
      const short = formatter(locale, timeZone, { day: "numeric", month: "numeric" });
      const long = formatter(locale, timeZone, { day: "numeric", month: "numeric", year: "numeric" });
      return `KW ${isoWeekOf(start, timeZone)} · ${short.format(start)} – ${long.format(last)}`;
    }
    case "1d":
      return formatter(locale, timeZone, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(start);
    default: {
      const end = new Date(bucketEnd(at, bucket, timeZone));
      const date = formatter(locale, timeZone, {
        weekday: "short",
        day: "numeric",
        month: "numeric",
        year: "numeric",
      })
        .formatToParts(start)
        .map((part) => (part.type === "weekday" ? trimDot(part.value) : part.value))
        .join("");
      const clock = formatter(locale, timeZone, {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
      return `${date}, ${clock.format(start)} – ${clock.format(end)}`;
    }
  }
}

/* ---- Die Achse ----------------------------------------------------------- */

export interface TimeTick {
  readonly at: number;
  readonly label: string;
}

export interface TickOptions {
  readonly from: number;
  readonly to: number;
  readonly timeZone: string;
  readonly locale: Locale;
  /** Die Breite der Zeichenfläche in CSS-Bildpunkten — sie entscheidet, wie viele Marken Platz haben. */
  readonly width: number;
  /**
   * Das Raster der Balken, wenn das Diagramm welche zeichnet — dann steht die
   * Marke eines Tages oder Monats **mittig** unter seinem Balken. `null` bei
   * Linien: Dort markiert sie den Beginn.
   */
  readonly centered: ChartBucket | null;
}

/** Bis hierher zeigt die Achse Uhrzeiten; darüber Tage, ab hier Monate. */
const HOURS_UP_TO = 36 * HOUR;
const DAYS_UP_TO = 45 * DAY;

/**
 * Die kleinste Stufe, bei der die Marken nebeneinander Platz haben — sonst
 * die gröbste. `spacing` ist der Platz, den eine Marke dieser Art braucht.
 */
function thin(levels: readonly number[], count: number, width: number, spacing: number): number {
  return levels.find((level) => (count / level) * spacing <= width) ?? levels[levels.length - 1]!;
}

export function timeTicks(options: TickOptions): readonly TimeTick[] {
  const span = options.to - options.from;
  if (span <= HOURS_UP_TO) return hourTicks(options);
  if (span <= DAYS_UP_TO) return dayTicks(options);
  return monthTicks(options);
}

/** Die erste volle Stunde ab `from` — nach Uhr der Anlage. */
function firstFullHour(from: number, timeZone: string): number {
  const parts = partsInZone(new Date(from), timeZone);
  const floor = zonedPartsToUtc({ ...parts, minute: 0, second: 0 }, timeZone).getTime();
  return floor >= from ? floor : floor + HOUR;
}

/**
 * Stunden an den Kanten. Ausgedünnt auf jede zweite, dritte, … zwölfte —
 * und zwar nach der **Uhr** (0, 3, 6, …), nicht nach der Zählung ab Anfang,
 * damit ein rollierendes Fenster ab 14:23 dieselben Marken trägt wie ein Tag.
 */
function hourTicks(options: TickOptions): TimeTick[] {
  const hours: { at: number; hour: number }[] = [];
  for (let at = firstFullHour(options.from, options.timeZone); at < options.to; at += HOUR) {
    const parts = partsInZone(new Date(at), options.timeZone);
    // Eine Zone mit halbstündiger Umstellung verschöbe die volle Stunde; dann neu einrasten.
    if (parts.minute !== 0) {
      at = firstFullHour(at, options.timeZone) - HOUR;
      continue;
    }
    hours.push({ at, hour: parts.hour });
  }
  /* Endet das Fenster an Mitternacht, bekommt auch die rechte Kante ihre Marke —
     „24:00" und nicht „00:00": Ohne sie endete die Achse eines Tages bei 18 Uhr,
     und die letzten sechs Stunden sahen aus wie ein Rand. */
  const end = partsInZone(new Date(options.to), options.timeZone);
  const endsAtMidnight = end.hour === 0 && end.minute === 0 && end.second === 0;
  if (endsAtMidnight && hours.length > 0) hours.push({ at: options.to, hour: 24 });
  /* Bei zusammengefassten Stunden steht eine Marke nur an einer Blockkante. */
  const block = mergedHoursOf(options.centered) ?? 1;
  const every = thin(
    [1, 2, 3, 4, 6, 12].filter((level) => level % block === 0),
    hours.length,
    options.width,
    56,
  );
  const clock = formatter(options.locale, options.timeZone, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return hours
    .filter((entry) => entry.hour % every === 0)
    .map((entry) => ({
      at: entry.at,
      label: entry.hour === 24 ? "24:00" : clock.format(new Date(entry.at)),
    }));
}

/** Wo eine Marke steht: mittig im Abschnitt oder an seinem Beginn — nie außerhalb des Fensters. */
function place(start: number, end: number, options: TickOptions, centered: boolean): number {
  const lo = Math.max(start, options.from);
  const hi = Math.min(end, options.to);
  return centered ? lo + (hi - lo) / 2 : lo;
}

/**
 * Tage. Bis acht Tage (eine Woche) mit Wochentag — „Mo 1.9." oder, wo das
 * nicht passt, nur „Mo". Darüber der Tag im Monat, ausgedünnt auf jeden
 * zweiten, fünften oder zehnten; der Erste trägt seinen Monat, damit ein
 * Fenster über die Monatsgrenze lesbar bleibt.
 */
function dayTicks(options: TickOptions): TimeTick[] {
  const days = localDaysBetween(new Date(options.from), new Date(options.to), options.timeZone);
  const centered = options.centered === "1d";
  const dayMonth = formatter(options.locale, options.timeZone, {
    day: "numeric",
    month: "numeric",
  });
  const weekday = formatter(options.locale, options.timeZone, { weekday: "short" });

  if (days.length <= 8) {
    const withDate = days.length * 60 <= options.width;
    return days.map((day) => ({
      at: place(day.start.getTime(), day.end.getTime(), options, centered),
      label: withDate
        ? `${trimDot(weekday.format(day.start))} ${dayMonth.format(day.start)}`
        : trimDot(weekday.format(day.start)),
    }));
  }

  const every = thin([1, 2, 5, 10], days.length, options.width, 26);
  const keep = (day: number): boolean =>
    every === 1 || (every === 2 ? day % 2 === 1 : day === 1 || day % every === 0);
  const kept = days
    .map((day, index) => ({ day, index, ofMonth: partsInZone(day.start, options.timeZone).day }))
    .filter((entry) => keep(entry.ofMonth));
  /* Der Erste eines Monats trägt immer eine Marke. Liegt die davor näher als
     der Abstand der übrigen — „30" direkt vor „1.10." —, stünden beide
     übereinander; die frühere weicht. */
  return kept
    .filter((entry, position) => {
      const next = kept[position + 1];
      return every === 1 || next === undefined || next.ofMonth !== 1 || next.index - entry.index >= every;
    })
    .map((entry) => ({
      at: place(entry.day.start.getTime(), entry.day.end.getTime(), options, centered),
      label: entry.ofMonth === 1 ? dayMonth.format(entry.day.start) : String(entry.ofMonth),
    }));
}

/**
 * Monate, auf drei Zeichen gekürzt („Jan", „Mär", „Sep"), ausgedünnt auf jeden
 * zweiten, dritten oder sechsten. Berührt das Fenster mehr als ein Jahr, trägt
 * der Januar — und die erste Marke — die Jahreszahl.
 *
 * Gekürzt und nicht `month: "short"` genommen: Die Kurzform hängt an den
 * ICU-Daten der Laufzeit — „Sept." im Browser, „Sep" in Node — und die
 * Schnellauswahl des Zeitraums schreibt „Mär" und „Sep". Zwei Schreibweisen
 * desselben Monats auf einer Seite sind eine zu viel.
 */
function monthTicks(options: TickOptions): TimeTick[] {
  const months = localMonthsBetween(new Date(options.from), new Date(options.to), options.timeZone);
  const centered = options.centered === "1M";
  const every = thin([1, 2, 3, 6], months.length, options.width, 48);
  const short = formatter(options.locale, options.timeZone, { month: "short" });
  const entries = months.map((month) => ({
    month,
    parts: partsInZone(month.start, options.timeZone),
  }));
  const multiYear = new Set(entries.map((entry) => entry.parts.year)).size > 1;

  return entries
    .filter((entry) => (entry.parts.month - 1) % every === 0)
    .map((entry, index) => {
      const name = trimDot(short.format(entry.month.start)).slice(0, 3);
      const withYear = multiYear && (entry.parts.month === 1 || index === 0);
      return {
        at: place(entry.month.start.getTime(), entry.month.end.getTime(), options, centered),
        label: withYear ? `${name} ${entry.parts.year}` : name,
      };
    });
}
